import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useConnector from './hooks/useConnector';
import { Button, Card, Spinner, ErrorAlert } from '../../components/shared';
import LinkAccountFlow from './components/LinkAccountFlow';
import RegisterFlow from './components/RegisterFlow';
import VdscLinkFlow from './components/VdscLinkFlow';
import VdscRegisterModeSelect from './components/VdscRegisterModeSelect';
import VdscRegisterFlow from './components/VdscRegisterFlow';
import VdscManualRegisterFlow from './components/VdscManualRegisterFlow';

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500',
];

function getInitials(name, code) {
  if (code && code.length <= 3) return code.substring(0, 2).toUpperCase();
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function getAvatarColor(code) {
  let hash = 0;
  for (let i = 0; i < (code || '').length; i++) hash = code.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function ProviderAvatar({ provider, size = 'md' }) {
  const [imgError, setImgError] = useState(false);
  const sizeClass = size === 'lg' ? 'h-20 w-20 rounded-full text-2xl' : 'h-12 w-12 rounded-xl text-sm';
  const imgSize = size === 'lg' ? 'h-12 w-12' : 'h-8 w-8';

  if (provider.logoUrl && !imgError) {
    return (
      <div className={`flex ${sizeClass} shrink-0 items-center justify-center bg-gray-50`}>
        <img
          src={provider.logoUrl}
          alt={provider.name}
          className={`${imgSize} rounded object-contain`}
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div className={`flex ${sizeClass} shrink-0 items-center justify-center ${getAvatarColor(provider.code)} font-semibold text-white`}>
      {getInitials(provider.name, provider.code)}
    </div>
  );
}

function SuccessScreen({ initResult, selectedProvider, onDone }) {
  const isPending = initResult?.status === 'REGISTERED'
    || initResult?.status === 'PENDING_KYC'
    || initResult?.status === 'PENDING_APPROVAL';
  const isPendingApproval = initResult?.status === 'PENDING_APPROVAL';
  const providerName = selectedProvider?.name || 'nhà cung cấp';

  const getMessage = () => {
    if (isPendingApproval) {
      return `Yêu cầu liên kết đã được gửi đến ${providerName}. Bạn sẽ được thông báo khi được duyệt.`;
    }
    if (isPending) {
      return `Tài khoản đang chờ ${providerName} xác minh. Bạn sẽ được thông báo khi hoàn tất.`;
    }
    return `Tài khoản ${providerName} đã được liên kết thành công với Simplize.`;
  };

  const getTitle = () => {
    if (isPendingApproval) return 'Yêu cầu đã gửi';
    if (isPending) return 'Đăng ký thành công';
    return 'Kết nối thành công';
  };

  return (
    <div className="mx-auto max-w-md">
      <Card>
        <div className="flex flex-col items-center gap-5 py-6">
          <div className={`flex h-16 w-16 items-center justify-center rounded-full ${isPending ? 'bg-yellow-50' : 'bg-green-50'}`}>
            {isPending ? (
              <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900">{getTitle()}</h3>
            <p className="mt-2 text-sm text-gray-500">{getMessage()}</p>
          </div>
          <Button onClick={onDone} className="w-full">
            Hoàn tất
          </Button>
        </div>
      </Card>
    </div>
  );
}

const STATUS_STYLES = {
  CONNECTED: 'bg-green-100 text-green-700',
  ACTIVE: 'bg-green-100 text-green-700',
  PENDING_KYC: 'bg-yellow-100 text-yellow-700',
  PENDING_OTP: 'bg-yellow-100 text-yellow-700',
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
  PENDING_AUTH: 'bg-yellow-100 text-yellow-700',
  VALIDATING: 'bg-yellow-100 text-yellow-700',
};

const STATUS_LABELS = {
  CONNECTED: 'Đã kết nối',
  ACTIVE: 'Đã kết nối',
  PENDING_KYC: 'Chờ xác minh',
  PENDING_OTP: 'Chờ OTP',
  PENDING_APPROVAL: 'Chờ duyệt',
  PENDING_AUTH: 'Chờ xác thực',
  VALIDATING: 'Đang xác thực',
};

function ProviderMenu({ onTrade, onViewDetail, onSyncStatus, onDisconnect, loading }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setConfirming(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => { setOpen(!open); setConfirming(false); }}
        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
      >
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <button
            type="button"
            onClick={() => { setOpen(false); onTrade(); }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5-3L16.5 18m0 0L12 13.5m4.5 4.5V4.5" />
            </svg>
            Giao dịch
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onViewDetail(); }}
            disabled={loading}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
            Xem chi tiết
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onSyncStatus(); }}
            disabled={loading}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
            Cập nhật trạng thái
          </button>
          {confirming ? (
            <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-2.5">
              <span className="text-xs text-red-600">Xác nhận?</span>
              <button
                type="button"
                onClick={() => { setOpen(false); setConfirming(false); onDisconnect(); }}
                disabled={loading}
                className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600 disabled:opacity-50"
              >
                Ngắt
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200"
              >
                Hủy
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="flex w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.181 8.68a4.503 4.503 0 011.903 6.405m-9.768-2.782L3.56 14.06a4.5 4.5 0 006.364 6.365l3.129-3.129m5.614-5.615l1.757-1.757a4.5 4.5 0 00-6.364-6.365l-3.129 3.129m0 0a4.503 4.503 0 00-1.903 6.405M8.7 17.737l.463-.464" />
              </svg>
              Ngắt kết nối
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const ACCOUNT_INFO_FIELDS = [
  { key: 'full_name', label: 'Họ tên' },
  { key: 'email', label: 'Email' },
  { key: 'email_verified', label: 'Email xác minh', type: 'bool' },
  { key: 'cell_phone', label: 'Số điện thoại' },
  { key: 'cell_phone_verified', label: 'SĐT xác minh', type: 'bool' },
  { key: 'gender', label: 'Giới tính', format: (v) => v === 1 ? 'Nam' : v === 0 ? 'Nữ' : '-' },
  { key: 'dob', label: 'Ngày sinh' },
  { key: 'nationality_label', label: 'Quốc tịch' },
  { key: 'id_type', label: 'Loại giấy tờ' },
  { key: 'id_no', label: 'Số giấy tờ' },
  { key: 'id_date_of_issue', label: 'Ngày cấp' },
  { key: 'id_place_of_issue', label: 'Nơi cấp' },
  { key: 'province_label', label: 'Tỉnh/Thành' },
  { key: 'permanent_address', label: 'Địa chỉ' },
  { key: 'is_kyc', label: 'KYC', type: 'bool' },
  { key: 'is_linked', label: 'Liên kết', type: 'bool' },
];

const BANK_INFO_FIELDS = [
  { key: 'bank_name', label: 'Ngân hàng' },
  { key: 'bank_branch', label: 'Chi nhánh' },
  { key: 'bank_account_no', label: 'Số TK' },
  { key: 'bank_account_name', label: 'Tên TK' },
];

function AccountInfoModal({ data, onClose }) {
  if (!data) return null;

  const bank = data.bank_account_info;
  const statusChanged = data.previousStatus !== data.newStatus;

  const renderValue = (field, source) => {
    const val = source[field.key];
    if (val == null || val === '') return <span className="text-gray-300">-</span>;
    if (field.type === 'bool') {
      return val ? (
        <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Yes</span>
      ) : (
        <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">No</span>
      );
    }
    if (field.format) return field.format(val);
    return String(val);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="mx-4 max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Thông tin tài khoản</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-5 p-6">
          {statusChanged && (
            <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-4 py-3 text-sm">
              <svg className="h-5 w-5 shrink-0 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-blue-700">
                Trạng thái cập nhật: <strong>{data.previousStatus}</strong> → <strong>{data.newStatus}</strong>
              </span>
            </div>
          )}

          <div>
            <h4 className="mb-3 text-sm font-medium text-gray-500">Thông tin cá nhân</h4>
            <div className="divide-y divide-gray-50 rounded-lg border border-gray-100">
              {ACCOUNT_INFO_FIELDS.map((field) => (
                <div key={field.key} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-gray-500">{field.label}</span>
                  <span className="text-sm font-medium text-gray-900">{renderValue(field, data)}</span>
                </div>
              ))}
            </div>
          </div>

          {bank && (
            <div>
              <h4 className="mb-3 text-sm font-medium text-gray-500">Ngân hàng</h4>
              <div className="divide-y divide-gray-50 rounded-lg border border-gray-100">
                {BANK_INFO_FIELDS.map((field) => (
                  <div key={field.key} className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-500">{field.label}</span>
                    <span className="text-sm font-medium text-gray-900">{renderValue(field, bank)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.contract_url && (
            <a
              href={data.contract_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              Xem hợp đồng
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const CONNECTION_TYPE_LABELS = {
  LINK: 'Liên kết',
  REGISTER: 'Đăng ký',
  API_KEY: 'API Key',
  INTERNAL: 'Paper Trading',
};

const ACCOUNT_STATUS_LABELS = {
  ACTIVE: 'Hoạt động',
  PENDING_KYC: 'Chờ xác minh KYC',
  PENDING_OTP: 'Chờ xác thực OTP',
  PENDING_APPROVAL: 'Chờ duyệt',
  PENDING_AUTH: 'Chờ xác thực',
  VALIDATING: 'Đang xác thực',
};

const ACCOUNT_STATUS_STYLES = {
  ACTIVE: 'bg-green-100 text-green-700',
  PENDING_KYC: 'bg-yellow-100 text-yellow-700',
  PENDING_OTP: 'bg-yellow-100 text-yellow-700',
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
  PENDING_AUTH: 'bg-yellow-100 text-yellow-700',
  VALIDATING: 'bg-yellow-100 text-yellow-700',
};

const PROVIDER_RESPONSE_LABELS = {};
ACCOUNT_INFO_FIELDS.forEach((f) => { PROVIDER_RESPONSE_LABELS[f.key] = f; });
BANK_INFO_FIELDS.forEach((f) => { PROVIDER_RESPONSE_LABELS[f.key] = f; });

function ConnectionDetailModal({ data, onClose }) {
  if (!data) return null;

  const accountStatusLabel = ACCOUNT_STATUS_LABELS[data.accountStatus] || data.accountStatus;
  const accountStatusStyle = ACCOUNT_STATUS_STYLES[data.accountStatus] || 'bg-gray-100 text-gray-600';

  const fields = [
    { label: 'Provider', value: data.providerName || data.providerCode },
    { label: 'Hình thức', value: CONNECTION_TYPE_LABELS[data.connectionType] || data.connectionType },
    { label: 'Trạng thái', value: STATUS_LABELS[data.status] || data.status },
    { label: 'Tài khoản', value: accountStatusLabel, badge: accountStatusStyle },
    { label: 'Mã tài khoản', value: data.brokerUserId },
    { label: 'Kết nối lúc', value: data.connectedAt ? new Date(data.connectedAt).toLocaleString('vi-VN') : null },
    { label: 'Ngắt kết nối lúc', value: data.disconnectedAt ? new Date(data.disconnectedAt).toLocaleString('vi-VN') : null },
  ];

  const providerResponse = data.providerResponse;

  const renderProviderValue = (key, val) => {
    if (val == null || val === '') return null;
    if (typeof val === 'object') return null;
    const fieldDef = PROVIDER_RESPONSE_LABELS[key];
    if (typeof val === 'boolean' || (fieldDef && fieldDef.type === 'bool')) {
      const boolVal = typeof val === 'boolean' ? val : Boolean(val);
      return boolVal
        ? <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Yes</span>
        : <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">No</span>;
    }
    if (fieldDef && fieldDef.format) return fieldDef.format(val);
    const str = String(val);
    if (str.length > 60) return str.substring(0, 57) + '...';
    return str;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="mx-4 max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Chi tiết kết nối</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-5 p-6">
          <div className="divide-y divide-gray-50 rounded-lg border border-gray-100">
            {fields.map((f) => f.value != null && (
              <div key={f.label} className="flex items-center gap-6 px-4 py-2.5">
                <span className="min-w-[120px] shrink-0 text-sm text-gray-500">{f.label}</span>
                {f.badge ? (
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${f.badge}`}>{f.value}</span>
                ) : (
                  <span className="text-sm font-medium text-gray-900">{f.value}</span>
                )}
              </div>
            ))}
          </div>

          {providerResponse && Object.keys(providerResponse).length > 0 && (
            <div>
              <h4 className="mb-3 text-sm font-medium text-gray-500">Thông tin từ nhà cung cấp</h4>
              <div className="divide-y divide-gray-50 rounded-lg border border-gray-100">
                {Object.entries(providerResponse).map(([key, val]) => {
                  const rendered = renderProviderValue(key, val);
                  if (rendered == null) return null;
                  const label = PROVIDER_RESPONSE_LABELS[key]?.label || key;
                  return (
                    <div key={key} className="flex items-center gap-6 px-4 py-2.5">
                      <span className="min-w-[120px] shrink-0 text-sm text-gray-500">{label}</span>
                      <span className="break-all text-sm font-medium text-gray-900">{rendered}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProviderList({ providers, activeConnections, onSelect, onViewDetail, onSyncStatus, onDisconnect, loading }) {
  const navigate = useNavigate();
  const [syncResult, setSyncResult] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const connectionMap = {};
  activeConnections.forEach((c) => { connectionMap[c.providerCode] = c; });

  const handleViewDetail = async (providerCode) => {
    const result = await onViewDetail(providerCode);
    if (result) setDetailData(result);
  };

  const handleSyncStatus = async (providerCode) => {
    const result = await onSyncStatus(providerCode);
    if (result) setSyncResult(result);
  };

  return (
    <>
      <div className="space-y-3">
        {providers.map((provider) => {
          const conn = connectionMap[provider.code];
          const isConnected = !!conn;
          const status = conn?.accountStatus || conn?.status || '';

          return (
            <Card key={provider.code}>
              <div className="flex items-center gap-4">
                <ProviderAvatar provider={provider} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                    {isConnected && (
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-500'}`}>
                        {STATUS_LABELS[status] || status}
                      </span>
                    )}
                  </div>
                  {provider.description && (
                    <p className="mt-0.5 truncate text-sm text-gray-500">{provider.description}</p>
                  )}
                  {provider.supportedAssetTypes?.length > 0 && (
                    <div className="mt-1 flex gap-1.5">
                      {provider.supportedAssetTypes.map((type) => (
                        <span key={type} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                          {type}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {isConnected ? (
                  <ProviderMenu
                    onTrade={() => navigate('/xchange/trading')}
                    onViewDetail={() => handleViewDetail(provider.code)}
                    onSyncStatus={() => handleSyncStatus(provider.code)}
                    onDisconnect={() => onDisconnect(provider.code)}
                    loading={loading}
                  />
                ) : (
                  <Button onClick={() => onSelect(provider)} size="sm">
                    Kết nối
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
        {providers.length === 0 && (
          <Card>
            <p className="py-6 text-center text-sm text-gray-500">Chưa có nhà cung cấp nào</p>
          </Card>
        )}
      </div>
      <ConnectionDetailModal data={detailData} onClose={() => setDetailData(null)} />
      <AccountInfoModal data={syncResult} onClose={() => setSyncResult(null)} />
    </>
  );
}

const CONNECTION_TYPE_CONFIG = {
  LINK: { label: 'Liên kết tài khoản', description: 'Kết nối tài khoản đã có' },
  REGISTER: { label: 'Đăng ký mới', description: 'Tạo tài khoản mới' },
  API_KEY: { label: 'API Key', description: 'Kết nối bằng API Key' },
  INTERNAL: { label: 'Paper Trading', description: 'Giao dịch giả lập' },
};

function ProviderActionScreen({ provider, onLink, onRegister, onBack, loading, error }) {
  const supportedTypes = provider.supportedConnectionTypes?.length > 0
    ? provider.supportedConnectionTypes
    : [provider.connectionType || 'LINK'];

  const handlers = { LINK: onLink, REGISTER: onRegister };

  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <div className="flex flex-col items-center gap-6 py-8">
          <ProviderAvatar provider={provider} size="lg" />
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900">Kết nối {provider.name}</h2>
            {provider.description && (
              <p className="mt-2 text-sm text-gray-500">{provider.description}</p>
            )}
          </div>

          <ErrorAlert error={error} className="w-full" />

          <div className="w-full space-y-2">
            {supportedTypes.map((type, idx) => {
              const config = CONNECTION_TYPE_CONFIG[type];
              const handler = handlers[type];
              return (
                <button
                  key={type}
                  onClick={handler}
                  disabled={loading || !handler}
                  className={`flex w-full items-center gap-4 rounded-xl border px-5 py-4 text-left transition-colors ${
                    handler
                      ? 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      : 'cursor-not-allowed border-gray-100 opacity-50'
                  }`}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-sm font-semibold text-blue-600">
                    {idx + 1}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{config?.label || type}</div>
                    <div className="text-sm text-gray-500">{config?.description || ''}</div>
                  </div>
                  {!handler && (
                    <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-400">Sắp ra mắt</span>
                  )}
                </button>
              );
            })}
          </div>

          <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700">
            Quay lại danh sách
          </button>
        </div>
      </Card>
    </div>
  );
}

export default function ConnectorPage() {
  const {
    providers,
    activeConnections,
    selectedProvider,
    flowState,
    initResult,
    catalogs,
    ekycSession,
    loading,
    loadingData,
    error,
    loadData,
    loadCatalogs,
    selectProvider,
    startLinkFlow,
    startRegisterFlow,
    submitLink,
    submitOtp,
    submitRegister,
    startVdscRegister,
    initVdscRegister,
    submitVdscOcr,
    submitVdscRegister,
    submitVdscOtp,
    disconnect,
    viewDetail,
    syncStatus,
    goBack,
    goHome,
    clearError,
    FLOW,
  } = useConnector();

  const isVdsc = selectedProvider?.code === 'VDSC';

  useEffect(() => {
    loadData();
  }, []);

  const CATALOG_FLOWS = [FLOW.LINK_FORM, FLOW.LINK_OTP, FLOW.REGISTER_FORM, FLOW.VDSC_EKYC_REVIEW, FLOW.VDSC_MANUAL_FORM];

  useEffect(() => {
    if (!loadingData && CATALOG_FLOWS.includes(flowState) && !catalogs) {
      loadCatalogs();
    }
  }, [loadingData, flowState, catalogs]);

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  const renderContent = () => {
    switch (flowState) {
      case FLOW.PROVIDER_SELECT:
        return (
          <ProviderActionScreen
            provider={selectedProvider}
            onLink={startLinkFlow}
            onRegister={isVdsc ? startVdscRegister : startRegisterFlow}
            onBack={goBack}
            loading={loading}
            error={error}
          />
        );

      case FLOW.LINK_FORM:
      case FLOW.LINK_OTP:
        if (isVdsc) {
          return (
            <VdscLinkFlow
              onSubmitLink={(body) => { clearError(); submitLink(body); }}
              onBack={goBack}
              loading={loading}
              error={error}
            />
          );
        }
        return (
          <LinkAccountFlow
            flowState={flowState}
            initResult={initResult}
            phonePostals={catalogs?.phonePostals || []}
            onSubmitLink={(id, phone, postal) => { clearError(); submitLink({ idNumber: id, phoneNumber: phone, phonePostal: postal }); }}
            onSubmitOtp={(vid, otp) => { clearError(); submitOtp(vid, otp); }}
            onBack={goBack}
            loading={loading}
            error={error}
            FLOW={FLOW}
          />
        );

      case FLOW.VDSC_REGISTER_MODE:
        return (
          <VdscRegisterModeSelect
            onSelect={(mode) => { clearError(); initVdscRegister(mode); }}
            onBack={goBack}
            loading={loading}
            error={error}
          />
        );

      case FLOW.VDSC_EKYC_OCR:
      case FLOW.VDSC_EKYC_REVIEW:
        return (
          <VdscRegisterFlow
            flowState={flowState}
            ekycSession={ekycSession}
            catalogs={catalogs}
            onSubmitOcr={(front, back) => { clearError(); submitVdscOcr(front, back); }}
            onSubmitRegister={(data) => { clearError(); submitVdscRegister(data); }}
            onSubmitOtp={(otp) => { clearError(); submitVdscOtp(otp); }}
            onBack={goBack}
            loading={loading}
            error={error}
          />
        );

      case FLOW.VDSC_MANUAL_FORM:
        return (
          <VdscManualRegisterFlow
            flowState={flowState}
            catalogs={catalogs}
            onSubmitRegister={(data) => { clearError(); submitVdscRegister(data); }}
            onSubmitOtp={(otp) => { clearError(); submitVdscOtp(otp); }}
            onBack={goBack}
            loading={loading}
            error={error}
          />
        );

      case FLOW.VDSC_REGISTER_OTP:
        if (ekycSession?.inputType === 'MANUAL') {
          return (
            <VdscManualRegisterFlow
              flowState={flowState}
              catalogs={catalogs}
              onSubmitRegister={(data) => { clearError(); submitVdscRegister(data); }}
              onSubmitOtp={(otp) => { clearError(); submitVdscOtp(otp); }}
              onBack={goBack}
              loading={loading}
              error={error}
            />
          );
        }
        return (
          <VdscRegisterFlow
            flowState={flowState}
            ekycSession={ekycSession}
            catalogs={catalogs}
            onSubmitOcr={(front, back) => { clearError(); submitVdscOcr(front, back); }}
            onSubmitRegister={(data) => { clearError(); submitVdscRegister(data); }}
            onSubmitOtp={(otp) => { clearError(); submitVdscOtp(otp); }}
            onBack={goBack}
            loading={loading}
            error={error}
          />
        );

      case FLOW.ACCOUNT_NOT_FOUND:
        return (
          <div className="mx-auto max-w-md">
            <Card>
              <div className="flex flex-col items-center gap-5 py-6">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-50">
                  <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900">Chưa có tài khoản</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Không tìm thấy tài khoản {selectedProvider?.name} với thông tin này. Bạn có thể đăng ký tài khoản mới.
                  </p>
                </div>
                <div className="flex w-full gap-3">
                  <Button onClick={goBack} variant="secondary" className="flex-1">
                    Quay lại
                  </Button>
                  <Button onClick={isVdsc ? startVdscRegister : startRegisterFlow} className="flex-1" loading={loading}>
                    Mở tài khoản
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );

      case FLOW.REGISTER_FORM:
        return (
          <RegisterFlow
            catalogs={catalogs}
            onSubmit={(data) => { clearError(); submitRegister(data); }}
            onBack={goBack}
            loading={loading}
            error={error}
          />
        );

      case FLOW.SUCCESS:
        return <SuccessScreen initResult={initResult} selectedProvider={selectedProvider} onDone={goHome} />;

      default:
        return (
          <ProviderList
            providers={providers}
            activeConnections={activeConnections}
            onSelect={selectProvider}
            onViewDetail={viewDetail}
            onSyncStatus={syncStatus}
            onDisconnect={disconnect}
            loading={loading}
          />
        );
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kết nối</h1>
          <p className="mt-1 text-sm text-gray-500">Quản lý kết nối với các nhà cung cấp dịch vụ đầu tư</p>
        </div>
        {flowState === FLOW.IDLE && (
          <Button
            size="sm"
            onClick={() => {
              const unconnected = providers.find((p) => !activeConnections.find((c) => c.providerCode === p.code));
              if (unconnected) selectProvider(unconnected);
            }}
          >
            <span className="flex items-center gap-1.5">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Kết nối
            </span>
          </Button>
        )}
      </div>
      {renderContent()}
    </div>
  );
}
